/* @noflow FIXME for real, I don't think the Notice works */
import * as React from 'react';
import { connect } from 'react-redux';
import invariant from 'invariant';
import { graphql } from 'react-apollo';
import { debounce } from 'lodash';

import { bindActionCreators } from 'redux';
import { REQUESTER_ROLE_OPTIONS } from 'admin/constants/AdminUpgrade';
import { saveLocalQuoteField, ORG_TYPE_STRING } from 'admin/reducers/adminUpgradePage';
import assertions from 'lib/assertions';
import Columns2 from 'components/Columns2.jsx';
import DatePickerInput from 'components/DatePickerInput.jsx';
import EventTracking from 'lib/EventTracking';
import Field from 'components/Field.jsx';
import FormElement from 'components/FormElement.jsx';
import Glyph from 'components/Glyph.jsx';
import GooglePlacesAutocompleteInput from 'components/GooglePlacesAutocompleteInput.jsx';
import Heading from 'components/Heading.jsx';
import Input from 'components/Input.jsx';
import InputValueConstants from 'constants/InputValueConstants';
import isSignedIn from 'user/queries/isSignedInQuery.gql';
import Label from 'components/Label.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import Link from 'components/Link.jsx';
import Notice from 'components/Notice.jsx';
import OrganizationPicker from 'components/OrganizationPicker';
import PhoneNumberMaskedInput from 'components/PhoneNumberMaskedInput.jsx';
import Radio from 'components/Radio.jsx';
import Row from 'components/Row.jsx';
import Select from 'components/Select.jsx';
import Text from 'components/Text.jsx';
import Tooltip from 'components/Tooltip.jsx';
import type { Fields } from 'admin/reducers/adminUpgradePage';

import './styles/AdminUpgradePlanDefinitionForm.scss';

function StartDateHelpIcon() {
  const tooltip = (
    <Tooltip>
      Remind Plans are annual.
      <br />
      Select the start of the billing cycle.
    </Tooltip>
  );

  return (
    <LayerTrigger placement="top" layer={tooltip}>
      <Glyph name="help" />
    </LayerTrigger>
  );
}

function AddressHelpIcon() {
  const tooltip = (
    <Tooltip>
      The organization’s
      <br />
      physical address.
    </Tooltip>
  );

  return (
    <LayerTrigger placement="top" layer={tooltip}>
      <Glyph name="help" />
    </LayerTrigger>
  );
}

export const ORG_RADIOS = [
  {
    key: 'school',
    name: 'School',
  },
  {
    key: 'district',
    name: 'District',
  },
  {
    key: 'other',
    name: 'Other',
  },
];

type Props = {
  fields: Fields,
  saveLocalQuoteField: (name: string, value: *) => void,
  organizationUuid: ?string,
  orgTypeString: ?string,
  ...isSignedInQuery,
};

type State = {
  orgTypeError: ?string,
  today: Date,
  studentEnrollmentFieldValue: ?number,
};

class AdminUpgradePlanDefinitionForm extends React.Component<Props, State> {
  state: State = {
    today: new Date(),
    studentEnrollmentFieldValue: null,
  };

  static trackingPrefix = 'admin_self_serve.define_plan';

  componentDidMount() {
    const { organizationUuid, fields } = this.props;

    if (organizationUuid && !fields.get('organizationUuid')) {
      this.props.saveLocalQuoteField('organizationUuid', organizationUuid);
      this.track('org_type.click');
    }

    if (!fields.get('startDate')) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.props.saveLocalQuoteField('startDate', tomorrow);
    }

    this.track('view');
  }

  componentWillUnmount() {
    const { fields, saveLocalQuoteField: saveField } = this.props;
    function prefillBillingField(requesterFieldName: string, billingFieldName: string) {
      if (!fields.get(billingFieldName)) {
        saveField(billingFieldName, fields.get(requesterFieldName));
      }
    }

    prefillBillingField('requesterFirstName', 'billingFirstName');
    prefillBillingField('requesterLastName', 'billingLastName');
    prefillBillingField('requesterEmail', 'billingEmail');
    prefillBillingField('requesterPhoneNumber', 'billingPhoneNumber');
  }

  handleOrgAddressFocus = () => {
    this.track('organization_address.click');

    if (this.props.fields.get('orgType')) {
      return;
    }

    this.setState({ orgTypeError: 'You must select an organization type.' });
  };

  track = (key: string) => {
    EventTracking.trackKey(`${AdminUpgradePlanDefinitionForm.trackingPrefix}.${key}`);
  };

  setOrgType = (type: string) => {
    this.props.saveLocalQuoteField('orgType', type);
    const orgType = this.props.fields.get('orgType');
    if (orgType && orgType !== type && (orgType === 'district' || type === 'district')) {
      this.props.saveLocalQuoteField('organizationUuid', null);
      this.props.saveLocalQuoteField('organizationAddress', null);
    }
    this.setState({ orgTypeError: null });
  };

  render() {
    const { fields } = this.props;
    const { orgTypeError } = this.state;

    const orgType = fields.get('orgType');

    const trackStartDateClick = debounce(() => {
      this.track('start_date.click');
    }, 100);

    return (
      <div>
        <Field error={orgTypeError}>
          <Label>Organization type</Label>
          {ORG_RADIOS.map(o => (
            <Radio
              inline
              name="org"
              key={o.key}
              label={o.name}
              onChange={() => this.setOrgType(o.key)}
              checked={orgType === o.key}
              onFocus={() => this.track('org_type.click')}
            />
          ))}
        </Field>
        {orgType && (
          <FormElement
            key={orgType}
            label={this.props.orgTypeString}
            onValidChange={o => {
              this.track('select_school.click');
              this.props.saveLocalQuoteField('organizationUuid', o.uuid);
            }}
            element={OrganizationPicker}
            elementProps={{
              type: orgType === 'district' ? 'district' : 'school',
              onClickInteraction: () => this.track('school_picker.click'),
              initialOrganizationUuid: fields.get('organizationUuid'),
            }}
          />
        )}
        <FormElement
          label={
            <span>
              {this.props.orgTypeString
                ? `${this.props.orgTypeString} street address`
                : 'Street address'}{' '}
              <AddressHelpIcon />
            </span>
          }
          validate={address => {
            if (!address || !address.streetAddress) {
              throw new Error(
                `Please select a complete street address for your ${orgType || 'organization'}`
              );
            }
            assertions.notEmpty(address);
            ['latitude', 'longitude', 'city', 'state', 'country', 'postalCode'].forEach(f =>
              invariant(address[f], `Must have a ${f}`)
            );
          }}
          onValidChange={address => this.props.saveLocalQuoteField('organizationAddress', address)}
          onInvalidChange={() => this.props.saveLocalQuoteField('organizationAddress', undefined)}
          element={GooglePlacesAutocompleteInput}
          elementProps={{
            onFocus: this.handleOrgAddressFocus,
            value: fields.get('organizationAddress'),
            placeholder: '1234 Remind Road, San Francisco, CA',
          }}
        />
        <Columns2>
          <FormElement
            label="Estimated teachers on staff"
            validate={e => {
              assertions.number(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.signedInt32(Number(e.target.value));
            }}
            onValidChange={e => this.props.saveLocalQuoteField('teachersCount', e.target.value)}
            onInvalidChange={() => this.props.saveLocalQuoteField('teachersCount', undefined)}
            element={Input}
            elementProps={{
              onFocus: () => this.track('teachers_on_staff.click'),
              defaultValue: fields.get('teachersCount'),
              value: fields.get('teachersCount'),
            }}
          />
          <FormElement
            label="Estimated student enrollment"
            validate={e => {
              assertions.number(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.signedInt32(Number(e.target.value));
              assertions.valueGreaterThan(5, e.target.value, 'Must include more than five people');
            }}
            onValidChange={e => this.props.saveLocalQuoteField('studentsCount', e.target.value)}
            onInvalidChange={() => this.props.saveLocalQuoteField('studentsCount', undefined)}
            element={Input}
            elementProps={{
              onFocus: () => {
                this.track('student_enrollment.click');
              },
              onBlur: e => {
                this.setState({
                  studentEnrollmentFieldValue: e.target.value,
                });
              },
              value: fields.get('studentsCount'),
            }}
          />
        </Columns2>
        {this.state.studentEnrollmentFieldValue &&
          this.state.studentEnrollmentFieldValue <= 5 && (
            <Row size="large">
              <Notice color="polar-bear" noShadow data-test="student-enrollment-too-small">
                <Text size="xsmall">
                  Remind plans are only available for schools and districts, but you can always
                  start with an individual account! Click <Link to="/">here</Link> to sign up for
                  free.
                </Text>
              </Notice>
            </Row>
          )}
        <Columns2>
          <FormElement
            label={
              <span>
                Start date <StartDateHelpIcon />
              </span>
            }
            validate={date => {
              assertions.withinSixMonths(
                date,
                'Please select a billing start date within the next 6 months.'
              );
            }}
            onValidChange={date => this.props.saveLocalQuoteField('startDate', date)}
            element={DatePickerInput}
            elementProps={{
              onFocus: trackStartDateClick,
              onClick: trackStartDateClick,
              value: fields.get('startDate'),
              min: this.state.today,
              full: true,
            }}
          />
        </Columns2>
        <section className="AdminUpgradePlanDefinitionForm-about">
          <Row size="small">
            <Heading level="2" levelStyle="6" divided>
              About You
            </Heading>
          </Row>
          {!this.props.user && (
            <Columns2>
              <FormElement
                label="First name"
                validate={e => {
                  assertions.notEmpty(e.target.value);
                  assertions.lengthLesserThanOrEqualTo(
                    e.target.value,
                    InputValueConstants.FIRST_NAME_LENGTH
                  );
                }}
                onValidChange={e =>
                  this.props.saveLocalQuoteField('requesterFirstName', e.target.value)
                }
                onInvalidChange={() =>
                  this.props.saveLocalQuoteField('requesterFirstName', undefined)
                }
                element={Input}
                elementProps={{
                  onFocus: () => this.track('requester_first_name.click'),
                  defaultValue: fields.get('requesterFirstName'),
                }}
              />
              <FormElement
                label="Last name"
                validate={e => {
                  assertions.notEmpty(e.target.value);
                  assertions.lengthLesserThanOrEqualTo(
                    e.target.value,
                    InputValueConstants.LAST_NAME_LENGTH
                  );
                }}
                onValidChange={e =>
                  this.props.saveLocalQuoteField('requesterLastName', e.target.value)
                }
                onInvalidChange={() =>
                  this.props.saveLocalQuoteField('requesterLastName', undefined)
                }
                element={Input}
                elementProps={{
                  onFocus: () => this.track('requester_last_name.click'),
                  defaultValue: fields.get('requesterLastName'),
                }}
              />
            </Columns2>
          )}
          <Columns2>
            <FormElement
              label="Role"
              validate={role => {
                assertions.notEmpty(role);
              }}
              onValidChange={role => this.props.saveLocalQuoteField('requesterRole', role)}
              onInvalidChange={() => this.props.saveLocalQuoteField('requesterRole', undefined)}
              element={Select}
              elementProps={{
                onFocus: () => this.track('requester_role.click'),
                options: REQUESTER_ROLE_OPTIONS,
                value: fields.get('requesterRole'),
              }}
            />
          </Columns2>
          <Columns2>
            <FormElement
              label="Your phone number"
              validate={e => {
                assertions.number(e.raw, 'Invalid phone number.');
                assertions.completePhoneNumber(e.raw, 'Invalid phone number.');
              }}
              onValidChange={e => this.props.saveLocalQuoteField('requesterPhoneNumber', e.raw)}
              onInvalidChange={() =>
                this.props.saveLocalQuoteField('requesterPhoneNumber', undefined)
              }
              element={PhoneNumberMaskedInput}
              elementProps={{
                defaultValue: fields.get('requesterPhoneNumber'),
                onFocus: () => this.track('requester_phone_number.click'),
              }}
            />
            {!this.props.user && (
              <FormElement
                showErrorAfterBlur
                label="Your official email address"
                validate={e => {
                  assertions.email(e.target.value);
                  assertions.notEmpty(e.target.value);
                }}
                onValidChange={e =>
                  this.props.saveLocalQuoteField('requesterEmail', e.target.value)
                }
                onInvalidChange={() => this.props.saveLocalQuoteField('requesterEmail', undefined)}
                element={Input}
                elementProps={{
                  onFocus: () => this.track('requester_email.click'),
                  defaultValue: fields.get('requesterEmail'),
                }}
              />
            )}
          </Columns2>
        </section>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    fields: state.adminUpgradePage.fields,
    orgTypeString: ORG_TYPE_STRING[state.adminUpgradePage.fields.get('orgType')],
  };
}

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ saveLocalQuoteField }, dispatch);
}

const withData = graphql(isSignedIn, {
  props: ({ data }) => ({ user: data.me, error: data.error }),
});

export { AdminUpgradePlanDefinitionForm };
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withData(AdminUpgradePlanDefinitionForm));
